import { useEffect, useRef, useState } from 'react';

const CHECK_INTERVAL_MS = 30 * 1000; // cada 30s
// Auto-reload cuando el usuario NO está mirando (tab oculta) es instantáneo.
// Si la tab está en foreground, esperamos este delay para no interrumpir un
// input activo. La wizard/forms persisten a localStorage cada cambio, así que
// un reload no borra el progreso — sólo la sesión efímera de UI (foco de input,
// scroll position). Antes eran 3 min: users desktop no llegaban a ver el
// banner discreto y el reload tardaba mucho — daba sensación de "nunca se
// actualiza". 45s es tolerable y visible.
const FOREGROUND_RELOAD_DELAY_MS = 45 * 1000; // 45s

let alreadyReloaded = false;
function safeReload() {
  if (alreadyReloaded) return;
  alreadyReloaded = true;
  // Marcador para que index.jsx no dispare OTRO reload por version check.
  try { sessionStorage.setItem('pm_auto_reload_at', String(Date.now())); } catch { /* noop */ }
  window.location.reload();
}

async function fetchVersion() {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data.version ?? null;
  } catch {
    return null;
  }
}

// Detecta nuevos deploys y auto-recarga la tab para evitar que usuarios
// queden trabados con bundles cacheados viejos (caso Martin/Grupo Paint:
// tab abierta durante horas, banner ignorado, seguían con código pre-fix).
//
// Política de reload:
//   - Tab oculta con update disponible → reload INMEDIATO (no molesta a nadie).
//   - Tab visible con update disponible → reload tras FOREGROUND_RELOAD_DELAY_MS
//     (3 min) para no cortar input activo. El banner sigue visible mientras tanto
//     para que el user pueda apretarlo antes.
export function useVersionCheck() {
  const initialVersion = useRef(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  // Segundos restantes hasta el auto-reload en foreground. El componente del
  // banner los usa para mostrar countdown ("Actualizando en 45s…"). Null
  // cuando no hay update pendiente.
  const [reloadInSeconds, setReloadInSeconds] = useState(null);
  const foregroundTimer = useRef(null);
  const countdownTimer = useRef(null);
  // Latch: si alguna vez detectamos update, marcamos para que el próximo
  // focus/visibility force reload inmediato — el user desktop que se va a
  // otra app y vuelve no debería ver un banner esperando 45s más.
  const updatePendingRef = useRef(false);

  useEffect(() => {
    fetchVersion().then(v => { initialVersion.current = v; });

    const startCountdown = () => {
      if (countdownTimer.current) return;
      let remaining = Math.round(FOREGROUND_RELOAD_DELAY_MS / 1000);
      setReloadInSeconds(remaining);
      countdownTimer.current = setInterval(() => {
        remaining -= 1;
        if (remaining <= 0) {
          clearInterval(countdownTimer.current);
          countdownTimer.current = null;
        }
        setReloadInSeconds(Math.max(0, remaining));
      }, 1000);
    };

    const scheduleForegroundReload = () => {
      if (foregroundTimer.current) return;
      startCountdown();
      foregroundTimer.current = setTimeout(() => {
        // Re-check antes de recargar — evita reload innecesario si en el
        // ínterin ya se aplicó el update por otro camino.
        fetchVersion().then(latest => {
          if (latest && initialVersion.current && latest !== initialVersion.current) {
            safeReload();
          }
        });
      }, FOREGROUND_RELOAD_DELAY_MS);
    };

    const check = async () => {
      const latest = await fetchVersion();
      if (latest && initialVersion.current && latest !== initialVersion.current) {
        updatePendingRef.current = true;
        setUpdateAvailable(true);
        // Notifica al resto de la app (útil para debug o handlers custom).
        try {
          window.dispatchEvent(new CustomEvent('pm:version-changed', {
            detail: { from: initialVersion.current, to: latest },
          }));
        } catch { /* noop */ }
        if (document.visibilityState === 'hidden') {
          safeReload();
        } else {
          scheduleForegroundReload();
        }
      }
    };

    // En focus / visible: si ya detectamos update, reload inmediato — el user
    // volvió a la tab (o está viendo la app), es el mejor momento para tomar
    // el bundle nuevo. Antes esperábamos el próximo tick del interval; ahora
    // es sincrónico.
    const handleFocus = () => {
      if (updatePendingRef.current) { safeReload(); return; }
      check();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        // Tab pasó a background. Si ya hay update pendiente, reload ahora
        // que nadie está mirando.
        if (updatePendingRef.current) { safeReload(); return; }
        fetchVersion().then(latest => {
          if (latest && initialVersion.current && latest !== initialVersion.current) {
            safeReload();
          }
        });
      } else {
        if (updatePendingRef.current) { safeReload(); return; }
        check();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    const timer = setInterval(() => check(), CHECK_INTERVAL_MS);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(timer);
      if (foregroundTimer.current) clearTimeout(foregroundTimer.current);
      if (countdownTimer.current) clearInterval(countdownTimer.current);
    };
  }, []);

  return { updateAvailable, reloadInSeconds, reloadNow: safeReload };
}
